import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { reportError } from '../../services/errorReporter.ts';

// Inline translations — ErrorBoundary sits outside I18nProvider so it cannot use useTranslation()
const errorTexts: Record<string, Record<string, string>> = {
  en: {
    title: 'Oops! Something went wrong',
    description:
      'The application encountered an unexpected error. This has been logged and we are looking into it. Try refreshing the page or going back to the homepage.',
    refresh: 'Refresh page',
    home: 'Go to homepage',
    support: 'If this problem persists, please contact support and mention the reference below.',
    reference: 'Reference',
  },
  nl: {
    title: 'Oeps! Er is iets misgegaan',
    description:
      'De applicatie is op een onverwachte fout gestuit. Dit is gelogd en we kijken ernaar. Probeer de pagina te verversen of terug te gaan naar de homepage.',
    refresh: 'Pagina verversen',
    home: 'Naar homepage',
    support:
      'Als dit probleem zich blijft voordoen, neem dan contact op met ondersteuning en vermeld de referentie hieronder.',
    reference: 'Referentie',
  },
};

function detectLang(): string {
  try {
    const userData = localStorage.getItem('af_user');
    if (userData) {
      const user = JSON.parse(userData);
      if (user.preferred_language === 'nl' || user.preferred_language === 'en')
        return user.preferred_language;
    }
  } catch {
    /* ignored — storage may be unavailable */
  }
  const browserLang = navigator.language?.toLowerCase() || '';
  return browserLang.startsWith('nl') ? 'nl' : 'en';
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  // Short, stable reference shown to the user so they can quote it to support.
  // The full error + stack are never rendered in the UI — only logged/reported.
  errorId: string | null;
}

class ErrorBoundary extends Component<Props, State> {
  declare state: State;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Generate a short, stable reference once per caught error so the user can
    // quote it to support. The raw error/stack stay out of the rendered UI.
    const errorId = Date.now().toString(36).toUpperCase();

    console.error(`ErrorBoundary caught an error [${errorId}]:`, error, errorInfo);

    this.setState({
      error,
      errorInfo,
      errorId,
    });

    reportError(error, { componentStack: errorInfo.componentStack ?? undefined, errorId });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      const txt = errorTexts[detectLang()] || errorTexts.en;
      return (
        <div
          className="min-h-screen bg-pt-cream flex items-center justify-center p-4"
          aria-live="assertive"
        >
          <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8" role="alert">
            <div className="flex justify-center mb-6">
              <div className="bg-pt-ruby-tint rounded-full p-4">
                <AlertTriangle className="w-12 h-12 text-pt-ruby" />
              </div>
            </div>

            <h1 className="text-3xl font-bold text-pt-forest text-center mb-4">{txt.title}</h1>

            <p className="text-gray-700 text-center mb-8">{txt.description}</p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={this.handleReload}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-pt-forest text-white rounded-lg hover:bg-opacity-90 transition-colors"
              >
                <RefreshCw className="w-5 h-5" />
                <span>{txt.refresh}</span>
              </button>

              <button
                onClick={this.handleGoHome}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-pt-gold text-pt-forest rounded-lg hover:bg-opacity-90 transition-colors"
              >
                <Home className="w-5 h-5" />
                <span>{txt.home}</span>
              </button>
            </div>

            <div className="mt-8 text-center text-sm text-gray-700">
              <p>{txt.support}</p>
              {this.state.errorId && (
                <p className="mt-2 font-mono text-xs bg-gray-100 p-2 rounded">
                  {txt.reference}: {this.state.errorId}
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
